import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Grid,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import PreviewIcon from '@mui/icons-material/Preview';
import WidgetsIcon from '@mui/icons-material/Widgets';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';
import { supabase } from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://chatai.comturk.com';

function WidgetSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [embedDialogOpen, setEmbedDialogOpen] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        widget_name: 'My Chat Widget',
        primary_color: '#4F46E5',
        text_color: '#FFFFFF',
        position: 'bottom-right',
        greeting_message: 'Merhaba! Size nasıl yardımcı olabilirim?',
        placeholder_text: 'Mesajınızı yazın...',
        require_name: false,
        require_email: false,
        require_phone: false,
        ai_enabled_default: true,
        show_branding: true,
        allowed_domains: [],
    });

    // Domain input state
    const [newDomain, setNewDomain] = useState('');
    const [domainError, setDomainError] = useState('');

    const getAuthToken = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token;
    }, []);

    // Fetch existing widget configuration
    const fetchConfig = useCallback(async () => {
        try {
            setLoading(true);
            const token = await getAuthToken();

            const response = await axios.get(`${BACKEND_URL}/widget/admin/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                setConfig(response.data);
                setFormData({
                    widget_name: response.data.widget_name || 'My Chat Widget',
                    primary_color: response.data.primary_color || '#4F46E5',
                    text_color: response.data.text_color || '#FFFFFF',
                    position: response.data.position || 'bottom-right',
                    greeting_message: response.data.greeting_message || 'Merhaba! Size nasıl yardımcı olabilirim?',
                    placeholder_text: response.data.placeholder_text || 'Mesajınızı yazın...',
                    require_name: response.data.require_name || false,
                    require_email: response.data.require_email || false,
                    require_phone: response.data.require_phone || false,
                    ai_enabled_default: response.data.ai_enabled_default ?? true,
                    show_branding: response.data.show_branding ?? true,
                    allowed_domains: response.data.allowed_domains || [],
                });
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Error fetching widget config:', error);
                setSnackbar({ open: true, message: 'Failed to load widget configuration', severity: 'error' });
            }
            // 404 means no config exists yet - that's OK
        } finally {
            setLoading(false);
        }
    }, [getAuthToken]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    // Validate domain format
    const isValidDomain = (domain) => {
        // Must start with http:// or https://
        if (!domain.startsWith('http://') && !domain.startsWith('https://')) {
            return false;
        }
        // Remove trailing slashes
        const cleanDomain = domain.replace(/\/+$/, '');
        // Basic URL structure check
        try {
            new URL(cleanDomain);
            return true;
        } catch {
            return false;
        }
    };

    // Add domain to list
    const handleAddDomain = () => {
        const cleanDomain = newDomain.trim().replace(/\/+$/, ''); // Remove trailing slashes

        if (!cleanDomain) {
            setDomainError('Please enter a domain');
            return;
        }

        if (!isValidDomain(cleanDomain)) {
            setDomainError('Domain must start with https:// or http://');
            return;
        }

        if (formData.allowed_domains.includes(cleanDomain)) {
            setDomainError('Domain already added');
            return;
        }

        setFormData(prev => ({
            ...prev,
            allowed_domains: [...prev.allowed_domains, cleanDomain]
        }));
        setNewDomain('');
        setDomainError('');
    };

    // Remove domain from list
    const handleRemoveDomain = (domainToRemove) => {
        setFormData(prev => ({
            ...prev,
            allowed_domains: prev.allowed_domains.filter(d => d !== domainToRemove)
        }));
    };

    // Save widget configuration
    const handleSave = async () => {
        // Validate: at least one domain required
        if (!formData.allowed_domains || formData.allowed_domains.length === 0) {
            setSnackbar({
                open: true,
                message: 'At least one allowed domain is required for security',
                severity: 'error'
            });
            return;
        }

        try {
            setSaving(true);
            const token = await getAuthToken();

            const response = await axios.post(`${BACKEND_URL}/widget/admin/config`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setConfig(response.data);
            setSnackbar({ open: true, message: 'Widget configuration saved successfully!', severity: 'success' });
        } catch (error) {
            console.error('Error saving widget config:', error);
            setSnackbar({ open: true, message: 'Failed to save widget configuration', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Regenerate widget token
    const handleRegenerateToken = async () => {
        if (!window.confirm('Are you sure? This will invalidate your current embed code.')) return;

        try {
            const token = await getAuthToken();
            const response = await axios.post(`${BACKEND_URL}/widget/admin/regenerate-token`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setConfig(prev => ({ ...prev, widget_token: response.data.widget_token }));
            setSnackbar({ open: true, message: 'Widget token regenerated!', severity: 'success' });
        } catch (error) {
            console.error('Error regenerating token:', error);
            setSnackbar({ open: true, message: 'Failed to regenerate token', severity: 'error' });
        }
    };

    // Copy embed code to clipboard
    const copyEmbedCode = () => {
        const embedCode = `<script 
  src="${BACKEND_URL}/widget/comturkai-widget.min.js"
  data-token="${config?.widget_token || 'YOUR_TOKEN'}">
</script>`;

        navigator.clipboard.writeText(embedCode);
        setSnackbar({ open: true, message: 'Embed code copied to clipboard!', severity: 'success' });
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <WidgetsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" fontWeight="bold">Widget Settings</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure and customize your embeddable chat widget
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column - Configuration */}
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SettingsIcon fontSize="small" />
                                Configuration
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={2}>
                                {/* Widget Name */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Widget Name"
                                        value={formData.widget_name}
                                        onChange={handleInputChange('widget_name')}
                                        helperText="Displayed in the widget header"
                                    />
                                </Grid>

                                {/* Colors */}
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Primary Color"
                                        type="color"
                                        value={formData.primary_color}
                                        onChange={handleInputChange('primary_color')}
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: formData.primary_color, mr: 1 }} />
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Text Color"
                                        type="color"
                                        value={formData.text_color}
                                        onChange={handleInputChange('text_color')}
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: formData.text_color, border: '1px solid #ccc', mr: 1 }} />
                                            ),
                                        }}
                                    />
                                </Grid>

                                {/* Position */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Widget Position</InputLabel>
                                        <Select
                                            value={formData.position}
                                            label="Widget Position"
                                            onChange={handleInputChange('position')}
                                        >
                                            <MenuItem value="bottom-right">Bottom Right</MenuItem>
                                            <MenuItem value="bottom-left">Bottom Left</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Greeting Message */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Greeting Message"
                                        multiline
                                        rows={2}
                                        value={formData.greeting_message}
                                        onChange={handleInputChange('greeting_message')}
                                        helperText="First message shown to visitors"
                                    />
                                </Grid>

                                {/* Placeholder Text */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Input Placeholder"
                                        value={formData.placeholder_text}
                                        onChange={handleInputChange('placeholder_text')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Pre-Chat Form Fields
                                    </Typography>
                                </Grid>

                                {/* Pre-chat form options */}
                                <Grid item xs={4}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.require_name} onChange={handleInputChange('require_name')} />}
                                        label="Require Name"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.require_email} onChange={handleInputChange('require_email')} />}
                                        label="Require Email"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.require_phone} onChange={handleInputChange('require_phone')} />}
                                        label="Require Phone"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Behavior
                                    </Typography>
                                </Grid>

                                {/* Behavior options */}
                                <Grid item xs={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.ai_enabled_default} onChange={handleInputChange('ai_enabled_default')} />}
                                        label="AI Enabled by Default"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.show_branding} onChange={handleInputChange('show_branding')} />}
                                        label="Show Branding"
                                    />
                                </Grid>

                                {/* Allowed Domains - REQUIRED */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WarningIcon color="warning" fontSize="small" />
                                        Allowed Domains * (Required)
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Security requirement:</strong> Enter the exact domain(s) where your widget will be installed.
                                            The widget will <strong>ONLY work</strong> on these domains.
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Format: https://yourdomain.com (include subdomains separately if needed)
                                        </Typography>
                                    </Alert>

                                    {/* Domain input */}
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Add Domain"
                                            placeholder="https://example.com"
                                            value={newDomain}
                                            onChange={(e) => { setNewDomain(e.target.value); setDomainError(''); }}
                                            error={!!domainError}
                                            helperText={domainError}
                                            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddDomain}
                                            startIcon={<AddIcon />}
                                            sx={{ minWidth: 100 }}
                                        >
                                            Add
                                        </Button>
                                    </Box>

                                    {/* Domain list */}
                                    {formData.allowed_domains.length > 0 ? (
                                        <Paper variant="outlined" sx={{ p: 1 }}>
                                            <List dense>
                                                {formData.allowed_domains.map((domain, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemText
                                                            primary={domain}
                                                            primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 14 }}
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton edge="end" size="small" onClick={() => handleRemoveDomain(domain)}>
                                                                <DeleteIcon fontSize="small" color="error" />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    ) : (
                                        <Alert severity="error">
                                            No domains added. You must add at least one domain to save the configuration.
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleSave}
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={20} /> : null}
                                >
                                    {saving ? 'Saving...' : 'Save Configuration'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - Embed Code & Status */}
                <Grid item xs={12} md={5}>
                    {/* Widget Token & Embed Code */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CodeIcon fontSize="small" />
                                Embed Code
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {config?.widget_token ? (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Widget Token
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={config.widget_token.substring(0, 16) + '...'}
                                                variant="outlined"
                                                sx={{ fontFamily: 'monospace' }}
                                            />
                                            <Tooltip title="Regenerate Token">
                                                <IconButton size="small" onClick={handleRegenerateToken}>
                                                    <RefreshIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: '#1a1a2e',
                                            color: '#10B981',
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                            overflow: 'auto',
                                            position: 'relative'
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={copyEmbedCode}
                                            sx={{ position: 'absolute', top: 8, right: 8, color: 'white' }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                            {`<script 
  src="${BACKEND_URL}/widget/comturkai-widget.min.js"
  data-token="${config.widget_token}">
</script>`}
                                        </pre>
                                    </Paper>

                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Add this code to your website's HTML, just before the closing <code>&lt;/body&gt;</code> tag.
                                    </Typography>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography color="text.secondary" gutterBottom>
                                        No widget configured yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Save your configuration to generate an embed code
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Preview */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PreviewIcon fontSize="small" />
                                Preview
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box
                                sx={{
                                    position: 'relative',
                                    height: 300,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: formData.position === 'bottom-left' ? 'flex-start' : 'flex-end',
                                    p: 2
                                }}
                            >
                                {/* Mock widget bubble */}
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        bgcolor: formData.primary_color,
                                        color: formData.text_color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'transform 0.2s',
                                    }}
                                >
                                    <WidgetsIcon />
                                </Box>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                This is a preview of how the widget bubble will appear on your website
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default WidgetSettings;
